import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { AccrualType, Member, VacationBalance } from '../../types';
import { UpdateVacationBalanceDto } from '../../services';

interface MemberBalanceOverrideProps {
  members: Member[];
  selectedMemberId: string;
  year: number;
  balance?: VacationBalance | null;
  balanceLoading?: boolean;
  isSubmitting?: boolean;
  serverError?: string | null;
  successMessage?: string | null;
  onMemberChange: (memberId: string) => void;
  onYearChange: (year: number) => void;
  onSubmit: (data: UpdateVacationBalanceDto) => void;
}

export const MemberBalanceOverride: React.FC<MemberBalanceOverrideProps> = ({
  members,
  selectedMemberId,
  year,
  balance,
  balanceLoading,
  isSubmitting,
  serverError,
  successMessage,
  onMemberChange,
  onYearChange,
  onSubmit,
}) => {
  const { t } = useTranslation('vacations');
  const [annualAllowance, setAnnualAllowance] = useState('');
  const [carriedOver, setCarriedOver] = useState('');
  const [accrued, setAccrued] = useState('');

  useEffect(() => {
    if (balance) {
      setAnnualAllowance(String(balance.annualAllowance));
      setCarriedOver(String(balance.carriedOver));
      setAccrued(String(balance.accrued));
    } else {
      setAnnualAllowance('');
      setCarriedOver('');
      setAccrued('');
    }
  }, [balance]);

  // FLEX balances derive their annualAllowance from the policy cap; the backend
  // rejects direct edits, so we disable the field and never send it.
  const isFlex = balance?.accrualType === AccrualType.FLEX || balance?.unlimited === true;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      annualAllowance:
        isFlex || annualAllowance.trim() === '' ? undefined : Number(annualAllowance),
      carriedOver: carriedOver.trim() === '' ? undefined : Number(carriedOver),
      accrued: accrued.trim() === '' ? undefined : Number(accrued),
    });
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl" data-testid="member-balance-override">
      {serverError && (
        <Alert variant="destructive" data-testid="balance-override-error">
          <AlertDescription>{serverError}</AlertDescription>
        </Alert>
      )}
      {successMessage && (
        <Alert variant="success" data-testid="balance-override-success">
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>{t('admin.balance.member')}</Label>
          <Select value={selectedMemberId} onValueChange={onMemberChange}>
            <SelectTrigger data-testid="balance-override-member">
              <SelectValue placeholder={t('admin.balance.selectMember')} />
            </SelectTrigger>
            <SelectContent>
              {members.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t('admin.balance.year')}</Label>
          <Select value={String(year)} onValueChange={(value) => onYearChange(Number(value))}>
            <SelectTrigger data-testid="balance-override-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedMemberId && (
        <>
          {balanceLoading ? (
            <p className="text-sm text-muted-foreground">{t('review.loadingBalance')}</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="balance-allowance">{t('admin.balance.annualAllowance')}</Label>
                <Input
                  id="balance-allowance"
                  type="number"
                  min={0}
                  value={annualAllowance}
                  onChange={(e) => setAnnualAllowance(e.target.value)}
                  disabled={isFlex}
                  data-testid="balance-override-allowance"
                />
                {isFlex && (
                  <p className="text-xs text-muted-foreground" data-testid="balance-override-flex-hint">
                    {t('admin.balance.flexAllowanceHint')}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="balance-carryover">{t('admin.balance.carriedOver')}</Label>
                <Input
                  id="balance-carryover"
                  type="number"
                  min={0}
                  value={carriedOver}
                  onChange={(e) => setCarriedOver(e.target.value)}
                  data-testid="balance-override-carryover"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="balance-accrued">{t('admin.balance.accrued')}</Label>
                <Input
                  id="balance-accrued"
                  type="number"
                  min={0}
                  step="0.01"
                  value={accrued}
                  onChange={(e) => setAccrued(e.target.value)}
                  data-testid="balance-override-accrued"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting || balanceLoading} data-testid="balance-override-submit">
              {isSubmitting ? t('form.submitting') : t('admin.balance.save')}
            </Button>
          </div>
        </>
      )}
    </form>
  );
};
